// Die Ego-Kamera als Ansicht des Spielerzustands: Position und Blick werden zwischen dem letzten
// und dem aktuellen Tick interpoliert, damit die Bewegung bei jeder Bildrate glatt ist. Die
// Simulation läuft mit 30 Hz, das Bild oft mit 60 oder mehr; ohne Interpolation ruckelte die
// Kamera im Takt der Simulation. Kein Spielzustand liegt in der Kamera; sie wird jedes Bild neu
// aus dem Zustand gesetzt.

import type { PerspectiveCamera } from 'three';
import { PLAYER, lerp, type PlayerState } from '@bauernhof/shared';

/** Augenpunkt und Blickrichtung, aus denen die Kamera gesetzt wird. */
export interface ViewPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

/**
 * Interpoliert zwischen zwei Spielerzuständen (`alpha` 0 = vorher, 1 = jetzt) und hebt den
 * Fußpunkt auf Augenhöhe. `yaw` wird von der Simulation nicht auf einen Bereich normiert
 * (ADR 0005), darum ist die lineare Interpolation auch beim Blick sprungfrei.
 */
export function interpolatePose(previous: PlayerState, current: PlayerState, alpha: number): ViewPose {
  return {
    x: lerp(previous.pos.x, current.pos.x, alpha),
    y: lerp(previous.pos.y, current.pos.y, alpha) + PLAYER.eyeHeight,
    z: lerp(previous.pos.z, current.pos.z, alpha),
    yaw: lerp(previous.yaw, current.yaw, alpha),
    pitch: lerp(previous.pitch, current.pitch, alpha),
  };
}

/** Eine eigenständige Kopie des Spielerzustands; die Simulation ersetzt `pos` je Tick. */
export function copyPlayerState(player: PlayerState): PlayerState {
  return {
    pos: { ...player.pos },
    vel: { ...player.vel },
    yaw: player.yaw,
    pitch: player.pitch,
    onGround: player.onGround,
    flying: player.flying,
  };
}

/**
 * Setzt die Kamera auf eine fertige Pose. Euler-Reihenfolge YXZ: erst um die Hochachse (yaw),
 * dann nicken (pitch), kein Rollen; die Kamera blickt in Three.js entlang −z, womit yaw = 0 wie
 * im Vertrag nach Norden zeigt und positive Werte nach links drehen.
 *
 * Getrennt von `updateCamera`, weil die Schulterkamera (Taste V) dieselbe Blickrichtung braucht,
 * aber einen anderen Standort: `schulterPose` schiebt den Augenpunkt nach hinten, gesetzt wird er
 * hier (client/src/render/schulterkamera.ts).
 */
export function applyPose(camera: PerspectiveCamera, pose: ViewPose): void {
  camera.position.set(pose.x, pose.y, pose.z);
  camera.rotation.set(pose.pitch, pose.yaw, 0, 'YXZ');
}
